import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { CartAllocations, PlanVersion, PlanVersions } from './distributionSlice';

interface CartAllocationState {
  allocations: CartAllocations;
  versions: PlanVersions;
  currentVersionId: string | null;
  selectedPlanId: string | null;
  selectedDate: string;
  hasUnsavedChanges: boolean;
  originalAllocations: CartAllocations;
  isLoading: boolean;
  error: string | null;
}

const initialState: CartAllocationState = {
  allocations: {},
  versions: {},
  currentVersionId: null,
  selectedPlanId: null,
  selectedDate: new Date().toISOString().split('T')[0],
  hasUnsavedChanges: false,
  originalAllocations: {},
  isLoading: false,
  error: null,
};

const cartAllocationSlice = createSlice({
  name: 'cartAllocation',
  initialState,
  reducers: {
    setAllocations: (state, action: PayloadAction<CartAllocations>) => {
      state.allocations = action.payload;
    },
    setVersions: (state, action: PayloadAction<PlanVersions>) => {
      state.versions = action.payload;
    },
    setCurrentVersionId: (state, action: PayloadAction<string | null>) => {
      state.currentVersionId = action.payload;
    },
    setSelectedPlanId: (state, action: PayloadAction<string | null>) => {
      state.selectedPlanId = action.payload;
    },
    setSelectedDate: (state, action: PayloadAction<string>) => {
      state.selectedDate = action.payload;
    },
    setHasUnsavedChanges: (state, action: PayloadAction<boolean>) => {
      state.hasUnsavedChanges = action.payload;
    },
    setOriginalAllocations: (state, action: PayloadAction<CartAllocations>) => {
      state.originalAllocations = action.payload;
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.isLoading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    updateAllocation: (
      state,
      action: PayloadAction<{
        cartId: string;
        productItemId: string;
        portionSize: string;
        value: number;
      }>
    ) => {
      const { cartId, productItemId, portionSize, value } = action.payload;
      
      if (state.allocations[cartId]) {
        state.allocations[cartId].allocations = state.allocations[
          cartId
        ].allocations.map((item) => {
          if (
            item.productItemId === productItemId &&
            item.portionSize === portionSize
          ) {
            return {
              ...item,
              directIssues: Math.max(0, value),
            };
          }
          return item;
        });
        
        state.hasUnsavedChanges = true;
      }
    },
    resetAllToSuggested: (state) => {
      Object.keys(state.allocations).forEach((cartId) => {
        if (state.originalAllocations[cartId]) {
          state.allocations[cartId].allocations = state.allocations[
            cartId
          ].allocations.map((item) => {
            const originalItem = state.originalAllocations[cartId]?.allocations.find(
              (a) =>
                a.productItemId === item.productItemId &&
                a.portionSize === item.portionSize
            );
            return {
              ...item,
              directIssues: originalItem?.directIssues || 0,
            };
          });
        }
      });
      state.hasUnsavedChanges = true;
    },
    resetToSuggested: (
      state,
      action: PayloadAction<{
        cartId: string;
        productItemId: string;
        portionSize: string;
      }>
    ) => {
      const { cartId, productItemId, portionSize } = action.payload;
      
      if (state.allocations[cartId] && state.originalAllocations[cartId]) {
        state.allocations[cartId].allocations = state.allocations[
          cartId
        ].allocations.map((item) => {
          if (
            item.productItemId === productItemId &&
            item.portionSize === portionSize
          ) {
            const originalItem = state.originalAllocations[cartId]?.allocations.find(
              (a) =>
                a.productItemId === productItemId &&
                a.portionSize === portionSize
            );
            return {
              ...item,
              directIssues: originalItem?.directIssues || 0,
            };
          }
          return item;
        });
        
        state.hasUnsavedChanges = true;
      }
    },
    saveVersion: (
      state,
      action: PayloadAction<{
        versionId: string;
        versionData: PlanVersion;
      }>
    ) => {
      const { versionId, versionData } = action.payload;
      const dateKey = state.selectedDate;
      
      state.versions = {
        ...state.versions,
        [dateKey]: {
          ...state.versions[dateKey],
          [versionId]: versionData,
        },
      };
      
      state.hasUnsavedChanges = false;
      state.originalAllocations = { ...state.allocations };
    },
    clearError: (state) => {
      state.error = null;
    },
    resetState: () => initialState,
  },
});

export const {
  setAllocations,
  setVersions,
  setCurrentVersionId,
  setSelectedPlanId,
  setSelectedDate,
  setHasUnsavedChanges,
  setOriginalAllocations,
  setLoading,
  setError,
  updateAllocation,
  resetAllToSuggested,
  resetToSuggested,
  saveVersion,
  clearError,
  resetState,
} = cartAllocationSlice.actions;

export default cartAllocationSlice.reducer;